import { useState, useEffect, useCallback, useRef } from 'react';
import { Languages } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = 'musicoul_translations';
const LANG_KEY = 'musicoul_lang';

function getCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}'); } catch { return {}; }
}
function setCache(cache) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (err) { console.error(err); }
}

export default function TranslateButton() {
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || 'en');
  const [open, setOpen] = useState(false);
  const [translating, setTranslating] = useState(false);
  const observerRef = useRef(null);
  const processingRef = useRef(false);
  const queueRef = useRef([]);

  const translateBatch = useCallback(async (nodes, targetLang) => {
    if (nodes.length === 0) return;
    const cache = getCache();
    const toTranslate = [];
    const cached = [];

    for (const node of nodes) {
      const key = `${targetLang}:${node.textContent.trim()}`;
      if (cache[key]) {
        cached.push({ node, text: cache[key] });
      } else {
        toTranslate.push(node);
      }
    }

    // Apply cached first
    cached.forEach(({ node, text }) => { node.textContent = text; });

    // Translate uncached in batches
    const batchSize = 12;
    for (let i = 0; i < toTranslate.length; i += batchSize) {
      const batch = toTranslate.slice(i, i + batchSize);
      const texts = batch.map(n => n.textContent.trim());
      const combined = texts.join('\n---SEP---\n');
      try {
        const res = await axios.post(`${API}/translate`, { text: combined, target_lang: targetLang });
        const parts = res.data.translated.split('---SEP---');
        batch.forEach((node, idx) => {
          const original = node.textContent.trim();
          const translated = parts[idx]?.trim();
          if (translated && translated !== original) {
            const key = `${targetLang}:${original}`;
            cache[key] = translated;
            node.textContent = translated;
          }
        });
      } catch (err) { console.error(err); }
    }
    setCache(cache);
  }, []);

  const getTranslatableNodes = useCallback(() => {
    const nodes = [];
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const p = node.parentElement;
        if (!p) return NodeFilter.FILTER_REJECT;
        const tag = p.tagName?.toLowerCase();
        if (['script', 'style', 'code', 'input', 'textarea', 'select', 'option'].includes(tag)) return NodeFilter.FILTER_REJECT;
        if (p.closest('[data-testid="translate-button"]')) return NodeFilter.FILTER_REJECT;
        const txt = node.textContent.trim();
        if (txt.length < 2 || /^[\d\s.,₹%+\-/:|@#&()*=<>{}[\]"'`~!?;_\\€$£¥]+$/.test(txt)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    let n;
    while ((n = walker.nextNode())) nodes.push(n);
    return nodes;
  }, []);

  const translatePage = useCallback(async (targetLang) => {
    if (targetLang === 'en') { window.location.reload(); return; }
    setTranslating(true);
    const nodes = getTranslatableNodes();
    await translateBatch(nodes.slice(0, 80), targetLang);
    setTranslating(false);
  }, [getTranslatableNodes, translateBatch]);

  // Auto-translate new content via MutationObserver
  useEffect(() => { document.documentElement.setAttribute('lang', lang); }, [lang]);
  useEffect(() => {
    if (lang === 'en') {
      if (observerRef.current) observerRef.current.disconnect();
      return;
    }

    const processQueue = async () => {
      if (processingRef.current || queueRef.current.length === 0) return;
      processingRef.current = true;
      const batch = queueRef.current.splice(0, 20);
      await translateBatch(batch, lang);
      processingRef.current = false;
      if (queueRef.current.length > 0) setTimeout(processQueue, 500);
    };

    const observer = new MutationObserver((mutations) => {
      const newNodes = [];
      for (const m of mutations) {
        for (const added of m.addedNodes) {
          if (added.nodeType === Node.TEXT_NODE) {
            const txt = added.textContent?.trim();
            if (txt && txt.length >= 2 && !/^[\d\s.,₹%+\-/:|@#&()]*$/.test(txt)) {
              newNodes.push(added);
            }
          } else if (added.querySelectorAll) {
            const walker = document.createTreeWalker(added, NodeFilter.SHOW_TEXT, {
              acceptNode: (node) => {
                const p = node.parentElement;
                if (!p) return NodeFilter.FILTER_REJECT;
                const tag = p.tagName?.toLowerCase();
                if (['script', 'style', 'code', 'input', 'textarea', 'select', 'option'].includes(tag)) return NodeFilter.FILTER_REJECT;
                if (p.closest('[data-testid="translate-button"]')) return NodeFilter.FILTER_REJECT;
                const txt = node.textContent?.trim();
                if (!txt || txt.length < 2) return NodeFilter.FILTER_REJECT;
                return NodeFilter.FILTER_ACCEPT;
              }
            });
            let n;
            while ((n = walker.nextNode())) newNodes.push(n);
          }
        }
      }
      if (newNodes.length > 0) {
        queueRef.current.push(...newNodes);
        setTimeout(processQueue, 300);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    observerRef.current = observer;

    // Translate current page
    translatePage(lang);

    return () => observer.disconnect();
  }, [lang, translateBatch, translatePage]);

  const handleSelect = (l) => {
    localStorage.setItem(LANG_KEY, l);
    setLang(l);
    setOpen(false);
    if (l === 'en') window.location.reload();
  };

  const langs = [
    { code: 'en', label: 'English', flag: 'EN' },
    { code: 'hi', label: 'हिन्दी', flag: 'हि' },
    { code: 'mr', label: 'मराठी', flag: 'म' },
  ];

  return (
    <div className="fixed top-20 right-3 z-40 sm:right-4" data-testid="translate-button">
      <button onClick={() => setOpen(!open)} disabled={translating}
        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shadow-lg transition-all ${lang !== 'en' ? 'bg-gradient-to-br from-[#EF4444] to-[#8B5CF6] ring-2 ring-[#8B5CF6]/30' : 'bg-[#12121E]/90 border border-[#8B5CF6]/20 hover:border-[#8B5CF6]/40'} disabled:opacity-50`}
        data-testid="translate-toggle">
        {translating ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Languages size={15} className="text-white" />}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 bg-[#12121E] border border-[#8B5CF6]/20 rounded-xl overflow-hidden shadow-2xl min-w-[150px]">
          {langs.map(l => (
            <button key={l.code} onClick={() => handleSelect(l.code)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${lang === l.code ? 'bg-gradient-to-r from-[#EF4444]/15 to-[#8B5CF6]/15 text-white font-medium' : 'text-[#B0B0BE] hover:bg-[#1A1A28]'}`}
              data-testid={`translate-${l.code}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${lang === l.code ? 'bg-gradient-to-r from-[#EF4444] to-[#8B5CF6] text-white' : 'bg-[#8B5CF6]/10 text-[#A78BFA]'}`}>{l.flag}</span>
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
